import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lightbulb, ThumbsUp, ThumbsDown, Trash, Clock } from "@phosphor-icons/react";

export default function MasterSuggestions() {
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("pending");
  const [reviewing, setReviewing] = useState(null);
  const [reviewComment, setReviewComment] = useState("");

  const load = async () => {
    try { setList((await api.get("/suggestions")).data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const doReview = async (id, vote) => {
    try {
      await api.post(`/suggestions/${id}/review`, { vote, admin_comment: reviewComment });
      toast.success(vote === "up" ? "Aprovada 👍" : "Recusada 👎");
      setReviewing(null); setReviewComment("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta sugestão permanentemente?")) return;
    try { await api.delete(`/suggestions/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = list.filter((s) =>
    tab === "pending" ? !s.admin_vote :
    tab === "up" ? s.admin_vote === "up" :
    s.admin_vote === "down"
  );
  const counts = {
    pending: list.filter((s) => !s.admin_vote).length,
    up: list.filter((s) => s.admin_vote === "up").length,
    down: list.filter((s) => s.admin_vote === "down").length,
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Admin</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1 flex items-center gap-3">
          <Lightbulb size={36} weight="duotone" className="text-[#F59E0B]" />
          Sugestões dos clientes
        </h1>
        <p className="text-sm text-[#5C5C59] mt-2">Avalie ideias enviadas — o cliente vê seu 👍/👎 e comentário.</p>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <button onClick={() => setTab("pending")} data-testid="msug-tab-pending" className={`px-4 py-2 text-sm font-semibold border ${tab === "pending" ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white border-[#E5E5E0] text-[#1A1A1A]"}`}>
          Pendentes ({counts.pending})
        </button>
        <button onClick={() => setTab("up")} data-testid="msug-tab-up" className={`px-4 py-2 text-sm font-semibold border ${tab === "up" ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white border-[#E5E5E0] text-[#1A1A1A]"}`}>
          👍 Aprovadas ({counts.up})
        </button>
        <button onClick={() => setTab("down")} data-testid="msug-tab-down" className={`px-4 py-2 text-sm font-semibold border ${tab === "down" ? "bg-[#0F4C3A] text-white border-[#0F4C3A]" : "bg-white border-[#E5E5E0] text-[#1A1A1A]"}`}>
          👎 Recusadas ({counts.down})
        </button>
      </div>

      {filtered.length === 0 && (
        <div className="bg-white border border-dashed border-[#E5E5E0] p-8 text-center text-sm text-[#5C5C59]">
          Nenhuma sugestão nesta aba.
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((s) => (
          <div key={s.id} className="bg-white border border-[#E5E5E0] shadow-brutal p-5" data-testid={`msug-row-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="font-bold text-sm">{s.title}</div>
                <p className="text-xs text-[#5C5C59] mt-1 whitespace-pre-wrap">{s.description}</p>
                <div className="text-[10px] text-[#5C5C59] mt-3 flex items-center gap-3">
                  <span><b>{s.created_by_name}</b></span>
                  <span>· <b>{s.company_name || "sem empresa"}</b></span>
                  <span>· <Clock size={10} weight="bold" className="inline" /> {new Date(s.created_at).toLocaleString("pt-BR")}</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                {s.admin_vote === "up" && (
                  <div className="flex flex-col items-center bg-[#DCFCE7] text-[#166534] border border-[#86EFAC] px-3 py-2">
                    <ThumbsUp size={20} weight="fill" />
                    <div className="text-[9px] uppercase tracking-widest font-bold mt-1">Aprovada</div>
                  </div>
                )}
                {s.admin_vote === "down" && (
                  <div className="flex flex-col items-center bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] px-3 py-2">
                    <ThumbsDown size={20} weight="fill" />
                    <div className="text-[9px] uppercase tracking-widest font-bold mt-1">Recusada</div>
                  </div>
                )}
                <button onClick={() => remove(s.id)} className="text-[#DC2626] hover:opacity-70" title="Excluir">
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>

            {s.admin_comment && (
              <div className="mt-3 pt-3 border-t border-[#E5E5E0] text-xs">
                <span className="font-bold text-[#0F4C3A]">Sua resposta:</span>{" "}
                <span className="text-[#1A1A1A]">{s.admin_comment}</span>
              </div>
            )}

            {reviewing === s.id ? (
              <div className="mt-3 pt-3 border-t border-[#E5E5E0] space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold">Comentário para o cliente (opcional)</Label>
                <Textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} data-testid={`msug-comment-${s.id}`} placeholder="Explique o motivo da decisão…" className="rounded-none min-h-[70px] text-xs" />
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" onClick={() => { setReviewing(null); setReviewComment(""); }} className="rounded-none">Cancelar</Button>
                  <Button onClick={() => doReview(s.id, "down")} data-testid={`msug-down-${s.id}`} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-none">
                    <ThumbsDown size={14} weight="bold" className="mr-1" /> Recusar
                  </Button>
                  <Button onClick={() => doReview(s.id, "up")} data-testid={`msug-up-${s.id}`} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                    <ThumbsUp size={14} weight="bold" className="mr-1" /> Aprovar
                  </Button>
                </div>
              </div>
            ) : !s.admin_vote ? (
              <div className="mt-3 pt-3 border-t border-[#E5E5E0] flex justify-end">
                <Button onClick={() => { setReviewing(s.id); setReviewComment(""); }} data-testid={`msug-review-${s.id}`} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none text-xs">
                  Avaliar
                </Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
